import { PrintProviderError, type PaperWidth } from "./types";

/**
 * Driverless printing for `QZ_NETWORK` printers (docs/restaurant-printing.md
 * §3a). The browser draws the artifact — so Arabic shaping, RTL and layout are
 * exactly what the browser renders — and the picture goes to the printer as
 * ESC/POS `GS v 0` raster blocks. QZ only forwards bytes to `host:port`.
 *
 * Two dead ends this replaces, both seen on a real 80 mm printer:
 * - QZ's own raw `format: "html"` draws with Java's old HTML engine: Arabic
 *   letters come out unjoined and reversed, and flex rows lose their text;
 * - QZ's raw `format: "image"` sends 24-dot `ESC *` strips whose line spacing
 *   the printer does not match, slicing lines and drifting the columns.
 *
 * The backend parses exactly this shape before signing (network-printer
 * policy): ESC @, full-width blocks of at most 256 rows, then EITHER ESC d 4
 * (no cutter: feed past the tear bar) OR GS V 66 0 (feed to this printer's own
 * cutter position and cut — no fixed guess, so no wasted paper). Change one
 * side and the other refuses to sign.
 */

/** Printable dots per row at 203 dpi (8 dots/mm). */
export const RASTER_DOTS: Record<PaperWidth, number> = {
  "80mm": 576,
  "58mm": 384,
};
export const RASTER_BLOCK_ROWS = 256;
export const RASTER_MAX_ROWS = 10_000;
/** Luminance below this prints as a black dot. */
const INK_THRESHOLD = 160;

const INITIALIZE = [0x1b, 0x40];
const FEED = [0x1b, 0x64, 0x04];
const FEED_TO_CUTTER_AND_CUT = [0x1d, 0x56, 0x42, 0x00];

/**
 * Encode an RGBA bitmap (as `ImageData.data`) whose width is exactly the
 * paper's dot width. Pure, so it is unit-testable without a canvas.
 */
export function encodeEscposRaster(input: {
  rgba: Uint8ClampedArray;
  width: number;
  height: number;
  cut: boolean;
}): Uint8Array {
  const { rgba, width, height } = input;
  if (width % 8 !== 0 || rgba.length !== width * height * 4) {
    throw new PrintProviderError("INVALID_DOCUMENT", "The rendered print image has an unexpected size.");
  }
  if (height < 1 || height > RASTER_MAX_ROWS) {
    throw new PrintProviderError("INVALID_DOCUMENT", "The rendered print image is empty or too long.");
  }
  const bytesPerRow = width / 8;
  const blocks = Math.ceil(height / RASTER_BLOCK_ROWS);
  const trailer = input.cut ? FEED_TO_CUTTER_AND_CUT : FEED;
  const out = new Uint8Array(INITIALIZE.length + blocks * 8 + bytesPerRow * height + trailer.length);
  out.set(INITIALIZE, 0);
  let offset = INITIALIZE.length;
  for (let top = 0; top < height; top += RASTER_BLOCK_ROWS) {
    const rows = Math.min(RASTER_BLOCK_ROWS, height - top);
    out.set([0x1d, 0x76, 0x30, 0x00, bytesPerRow & 0xff, bytesPerRow >> 8, rows & 0xff, rows >> 8], offset);
    offset += 8;
    for (let y = top; y < top + rows; y += 1) {
      for (let column = 0; column < bytesPerRow; column += 1) {
        let byte = 0;
        for (let bit = 0; bit < 8; bit += 1) {
          const i = (y * width + column * 8 + bit) * 4;
          const alpha = rgba[i + 3]! / 255;
          // Transparent reads as paper (white).
          const luminance =
            (rgba[i]! * 0.299 + rgba[i + 1]! * 0.587 + rgba[i + 2]! * 0.114) * alpha + 255 * (1 - alpha);
          if (luminance < INK_THRESHOLD) byte |= 0x80 >> bit;
        }
        out[offset++] = byte;
      }
    }
  }
  out.set(trailer, offset);
  return out;
}

export function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
}

/**
 * Draw a complete standalone print document in a hidden frame and snapshot it
 * at the paper's dot width. The document is self-contained (inline CSS, system
 * fonts), which is what lets an SVG `foreignObject` reproduce it faithfully.
 */
export async function rasterizePrintDocument(input: {
  documentHtml: string;
  paperWidth: PaperWidth;
  cut: boolean;
}): Promise<string> {
  const dots = RASTER_DOTS[input.paperWidth];
  const frame = document.createElement("iframe");
  frame.setAttribute("aria-hidden", "true");
  frame.tabIndex = -1;
  frame.style.cssText = "position:fixed;left:-10000px;top:0;width:400px;height:10px;border:0;visibility:hidden";
  document.body.appendChild(frame);
  try {
    await new Promise<void>((resolve, reject) => {
      frame.onload = () => resolve();
      frame.onerror = () => reject(new PrintProviderError("INVALID_DOCUMENT", "The print document could not be drawn."));
      frame.srcdoc = input.documentHtml;
    });
    const doc = frame.contentDocument;
    if (!doc?.body) {
      throw new PrintProviderError("INVALID_DOCUMENT", "The print document could not be drawn.");
    }
    await doc.fonts?.ready;
    // The document sets its own content width in mm; measure what it laid out.
    const cssWidth = Math.ceil(doc.body.getBoundingClientRect().width);
    const cssHeight = Math.ceil(doc.documentElement.scrollHeight);
    if (cssWidth < 1 || cssHeight < 1) {
      throw new PrintProviderError("INVALID_DOCUMENT", "The print document is empty.");
    }
    const scale = dots / cssWidth;
    const height = Math.max(1, Math.ceil(cssHeight * scale));
    const markup = new XMLSerializer().serializeToString(doc.documentElement);
    // A viewBox scales the vector drawing itself, so text stays sharp at the
    // printer's resolution instead of being an enlarged screen bitmap.
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${dots}" height="${height}" viewBox="0 0 ${cssWidth} ${cssHeight}"><foreignObject x="0" y="0" width="${cssWidth}" height="${cssHeight}">${markup}</foreignObject></svg>`;
    const image = new Image();
    image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
    await image.decode();
    const canvas = document.createElement("canvas");
    canvas.width = dots;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) {
      throw new PrintProviderError("INVALID_DOCUMENT", "The print document could not be drawn.");
    }
    context.fillStyle = "#fff";
    context.fillRect(0, 0, dots, height);
    context.drawImage(image, 0, 0, dots, height);
    const pixels = context.getImageData(0, 0, dots, height);
    return bytesToBase64(encodeEscposRaster({ rgba: pixels.data, width: dots, height, cut: input.cut }));
  } catch (error) {
    if (error instanceof PrintProviderError) throw error;
    throw new PrintProviderError("INVALID_DOCUMENT", "The print document could not be drawn.", error);
  } finally {
    frame.remove();
  }
}
